import { errorJsonResponse, errorResponseBody } from "../utils/objectTypeConstants";

export const getStartedContent = {
    AuthenticationGuide: [
        {
            heading: 'Authentication',
            info: [
                "Authenticating with the SafeSend APIs is a simple process to ensure proper security within your firm. We offer multiple layers of authentication to make use of our APIs. All API operations will start in the SafeSend Developers Section. To access this page you must be a system administrator or have the API Management role assigned to the user, to learn more about how to do that you can visit our help center here. The Developer Section is where you can subscribe to our webhooks and gain access to your API subscription keys.",
                "In addition to the Developer Section, we have the API Developer role in the SafeSend User Management. This is the role (in addition to system admin) that is necessary to make API calls to our endpoints. When attempting to authenticate, we will check if you have the API Developer or System Admin role or we will not allow to create a login token to call the APIs. This is ideal for firms that want API development but do not want to grant extra permissions to the API developers.",
                "SafeSend also provides a workaround for the API developer role for firms with specific use cases. Within the SafeSend Suite Settings you can enable the API Access override. When this is enabled, we will ignore the SafeSend Developer role restriction and allow any user to make API calls as long as they still have the subscription key. This is disabled by default for all firms. Please proceed with caution when enabling this setting."
            ]
        }
    ],
    webhooksGuide: [
        {
            heading: 'What Are Webhooks?',
            info: ['A webhook is a method of augmenting or altering the behavior of a web application with custom callbacks. These callbacks are triggered by specific events (e.g. a tax return is signed or a source document is uploaded by your client) and are sent to a designated URL that you set, allowing real-time data sharing between SafeSend and your application. Unlike traditional APIs, which require continuous polling to check for updates, webhooks push information as soon as an event occurs.']
        },
        {
            heading: 'How Do Webhooks Work?',
            items: [
                { title: 'Event Occurrence', description: 'An event happens within the source application. This could be a tax returned being signed from SafeSend Returns or document being uploaded to a document request list in Gather.' },
                { title: 'Webhook Triggered', description: 'SafeSend detects the event and sends a response with relevant data from SafeSend to the predefined webhook URL, which is the endpoint of your application.' },
                { title: 'Data Payload', description: 'The response contains a data payload that provides detailed information about the event. This payload is in JSON format.' },
                { title: 'Processing the Data', description: 'You receive the payload and perform predefined actions based on the event details. This could include updating records, triggering notifications, or routing delivered files.' },
            ]
        },
        {
            heading: 'Benefits of Webhooks',
            items: [
                { title: 'Real-Time Data', description: 'Webhooks provide immediate notifications when an event occurs, ensuring that your application has the most up-to-date information without delays.' },
                { title: 'Reduced Server Load', description: 'Since webhooks push data only when an event occurs, they eliminate the need for constant polling, reducing server load and bandwidth usage.' },
                { title: 'Automated Workflows', description: 'By automating responses to specific events, webhooks streamline workflows and improve operational efficiency.' },
                { title: 'Scalability', description: 'Webhooks facilitate seamless integration between various applications and services, allowing your systems to scale and adapt quickly.' },
            ]
        },
        {
            heading: 'Implementing Webhooks',
            info: ["To set up webhooks, follow these general steps:"],
            items: [
                { title: 'Define your workflow with SafeSend’s events', description: 'SafeSend offers many different webhooks that trigger on different events. Those can be found in the SafeSend Developer Section. We currently have webhooks for Returns, Signatures, Organizers, Gather, and Exchange. Please consult the detailed webhook documentation for more details on the payload and event triggers.' },
                { title: 'Configure Webhook URL', description: 'You must set up an endpoint URL that can receive the events payload and is listening for incoming webhook requests. This is something that you provide to us and it must be able to send HTTP requests back to us to confirm you received it. This is mandatory.' },
                { title: 'Security Measures', description: 'Implement the security measures by creating an API key that you give to us in the Developer Section. We will pass the API Key you give us in the headers of each event you send us. This ensures that anything sent to your endpoint is coming from us.' },
                { title: 'Handle Incoming Data', description: 'Write code to handle and process the data payload sent by us. More detailed information about the data we send can be found in the specific webhook documentation section.' },
            ]
        },
        {
            heading: "Understanding the Developer Section",
            info: ["To use the SafeSend Webhooks you will have to use the SafeSend Developer Section. The Developer Section is where you will subscribe to the webhooks, monitor connections, and check logs. Below is and explanation of each part of the Developer Section."],
            items: [
                { description: 'The events that you can subscribe to. They are sectioned by the SafeSend product which they are associated with. The event names gives a description of when the webhook will trigger a payload response.' },
                { description: 'This section is all the emails subscribed to the event. If the webhook connection errors for some reason, those emails will be notified. This can be configured in the “Configure Email” section.' },
                { description: 'This allows to you enable or disable a webhook connection. This can only be enabled after the webhook is configured via the “Configure API” section. If it is enabled, the webhook can be disabled at anytime, and if it is, you will no longer receive payloads on event triggers.' },
                {
                    description: 'The Service Status shows the current status of the webhook. It will function between Stopped, Resume, and Active.',
                    subItems: [
                        {
                            title: 'Stopped',
                            description: 'The webhook is currently turned off. Your API will not receive any payloads on the event trigger.',

                        },
                        {
                            title: 'Resume',
                            description: 'The webhook is currently paused and is no longer sending payloads to you API. This happens during a temporary error with the most common case being we are no longer receiving response messages from your API when we send you a payload.'
                        },
                        {
                            title: 'Active',
                            description: 'The webhook is currently on and is sending payloads to your API.'
                        },
                    ]
                },
                {
                    description: 'This is where you will configure your API to connect with the webhook. This configuration must be done before you can begin to receive payloads. Below is a description of the items within “Configure API.”',
                    subItems: [
                        {
                            title: 'API Endpoint',
                            description: 'This is where we will send the payload on each event trigger. You must provide this.'
                        },
                        {
                            title: 'API Key',
                            description: 'This is a key that you create and that you provide to us. We will use this to authenticate when we send the payload. We pass the payload with the API Key in the headers. With this, you can be sure that any data sent to your endpoint is coming from us.'
                        },
                        {
                            title: 'Test Connection',
                            description: 'After you have configured the above you must test the connection. We will send a basic payload with sample data. If we can successfully send it to your endpoint we will allow you to activate the webhook.'
                        },
                        {
                            title: 'Unique Arguments',
                            description: 'Here you can assign custom arguments to the webhook. Anything added here will be sent with each payload in the unique arguments section.'
                        }
                    ]
                },
                {
                    description: 'Here is where you assign emails to receive notifications when the webhooks errors.',
                },
                {
                    description: 'This is where you can download a log in the case of the webhook having an error. If there is no error, no log will be downloaded.',
                },
                {
                    description: 'Here you can download a PDF version of the response models of the payloads.',
                },
                {
                    description: 'This is where the subscription keys are stored to call our API endpoints.',
                },
            ]
        },
        {
            heading: 'Conclusion',
            info: ['Webhooks are an essential tool for modern web development, enabling real-time communication and automation between applications. By integrating webhooks into your workflow, you can enhance the responsiveness, efficiency, and scalability of your systems.This primer serves as a foundation for understanding and implementing webhooks in your projects, paving the way for more dynamic and interactive applications.']
        },
        {
            heading: 'FAQ',
            items: [
                {
                    question: "Are their IP addresses available where the webhook response payloads will come from?",
                    answer: "Due to SafeSend’s commitment to security our IP addresses will change frequently. These changes do not always change within a specific range. This will cause your webhook implementation to fail periodically with significant delays. We will not providing IP addresses for our webhooks for this reason."
                }
            ]
        }
    ],

    APIGuide: [
        {
            heading: 'Introduction to API Public Endpoints',
            info: ["In today's interconnected digital ecosystem, APIs (Application Programming Interfaces) play a crucial role in enabling seamless communication between different software applications. Among the various types of API endpoints, public endpoints stand out for their ability to facilitate open access to specific functionalities and data. This primer provides an introduction and overview of API public endpoints, explaining their purpose, how they work, and the benefits they offer to developers."]
        },
        {
            heading: 'What Are API Public Endpoints?',
            info: ['API public endpoints are specific URL addresses exposed by an API that allow external applications to interact with SafeSend functions and data. These endpoints are designed to be accessible by anyone with an internet connection, making them a powerful tool for developers looking to integrate their firms processes with SafeSend services and data.',]
        },
        {
            heading: 'How Do API Public Endpoints Work?',
            items: [
                { title: 'Endpoint Definition', description: 'We have created many API endpoints that correspond with functions you can perform in SafeSend via the UI. A list of what functions you can perform via the API can be found in more detail in the endpoints section of the developer portal.' },
                { title: 'HTTP Requests', description: 'External applications send HTTP requests (GET, POST, PUT, DELETE, etc.) to these endpoints. The type of request determines the action to be performed.' },
                { title: 'Authentication and Authorization', description: 'To access our public endpoints you must provide a subscription key. This subscription key is found in the SafeSend Developers Section(link), at the bottom of the page. In addition, with each API call you make, we will check the users SafeSend permissions. They must have the API Developer permission to successfully make calls. This can be set in SafeSend User Management.' },
                { title: 'Data Exchange', description: 'The API processes the request and responds with the relevant data or action result. For example we will return a list of all delivered returns or archive a return.' },
            ]
        },
        {
            heading: 'Benefits of Using API Public Endpoints',
            items: [
                {
                    title: 'Accessibility',
                    description: "Public endpoints provide easy access to the API's functionalities and data, enabling developers to quickly integrate these resources into their applications."
                },
                {
                    title: 'Interoperability',
                    description: "By offering standardized access points, public endpoints facilitate interoperability between different systems and platforms, fostering a more connected digital environment."
                },
                {
                    title: 'Scalability',
                    description: "Public endpoints allow developers to build scalable applications that can leverage external services without the need for extensive backend infrastructure."
                },
                {
                    title: 'Innovation',
                    description: "By providing open access to our APIs, we hope to encourage innovation and creativity within the our community, leading to new and unexpected uses of our services."
                },
            ]
        },
        {
            heading: 'Implementing SafeSend Endpoints',
            info: ['To set up SafeSend API endpoints, follow these general steps:'],
            items: [
                {
                    description: 'Navigate to the SafeSend Developer Section and scroll down to the bottom of the page to find your account’s subscription key.',
                    subItems: [
                        { description: 'We provide each account two subscriptions keys, a primary and a secondary. Both can be used to make API calls.' },
                        { description: 'If ever a subscription key is regenerated the previous key will no longer work when calling SafeSend APIs.' }
                    ]
                },
                {
                    description: 'After you have the subscription key, call the Login API (https://api.safesend.com/v2/account/login) by passing the subscription key in the headers to get your bearer token.',
                    subItems: [
                        { description: 'All of the SafeSend APIs will check the user’s permissions to access certain parts of the product. The login used for the APIs should have proper permissions for the task.' }
                    ]
                },
                {
                    description: 'With the bearer token from the API you can now make all other API calls by passing the subscription key and the login token in the headers.',
                    subItems: [
                        { description: 'To pass the bearer token you must follow the format of [Bearer <token>].' }
                    ]
                },
            ]
        },
        {
            heading: 'Callback Mechanism',
            items: [
                {
                    title: 'Overview',
                    description: 'Some of SafeSend’s API endpoints support a callback mechanism. APIs that support a callback mechanism have that mentioned in the API Endpoint Documentation. Some of SafeSend’s APIs may take some time to complete. This is often due to load balancing in the system to ensure all actions are handled safely and appropriately. In these cases the SafeSend API will return with a 202 response meaning the request was successful but the operation has not started yet. When the system does complete the request it will send the new response to you via the callback mechanism.'
                },
                {
                    title: 'How to set up the callback mechanism',
                    description: 'The callback mechanism can be configured by passing a callbackUri in the request body and a callback-api-key in the request header. The callbackUri is where we will send the response code upon action completion. We will pass the callback-api-key back to you in the response. This will ensure that the response is coming from us. Once the request is processed, the response will be posted to the configured callback endpoint URL. This is not mandatory for any of the API calls but if you do not use it you will not receive API call responses open operation completion.'
                },
            ]
        }
    ],
    apiErrors: [
        {
            heading: "Introduction to Common Errors When Using Public APIs",
            info: [
                "Public APIs are essential tools for developers, enabling integration with external services and data sources. However, working with public APIs can sometimes lead to errors that disrupt the smooth functioning of your application. Understanding these errors and how to address them is crucial for building robust and reliable workflows. This primer provides an overview of common errors encountered when using public APIs, along with a list of common HTTP error codes and their descriptions."
            ]
        },
        {
            heading: "Common API Endpoint Error Codes and Their Descriptions",
            info: [
                "Below are common error codes that you may see and the reason why you are seeing them. Errors in the 400 range (4xx) are client errors. These are errors due to bad API calls or improper permissions. You will need to correct these on your side. Errors in the 500 range (5xx) are errors on SafeSend’s side and need to be corrected by us. You will rarely see a 500 error but if you do we will work quickly to resolve it."
            ],
            items: [
                {
                    title: "4xx Client Errors",
                    subItems: [
                        {
                            title: "400 Bad Request",
                            description: "The server cannot process the request due to an error in the request (wrong syntax or missing necessary inputs)."
                        },
                        {
                            title: "401 Unauthorized",
                            description: "Authentication is required and has failed or has not been provided. Ensure you have a proper login token or subscription key."
                        },
                        {
                            title: "403 Forbidden",
                            description: "You do not have access rights to the content. Please ensure you have the correct user permissions applied to the account making the API call."
                        },
                        {
                            title: "404 Not Found",
                            description: "We cannot find the requested resource. Please verify the URL you are calling is correct."
                        },
                        {
                            title: "429 Too Many Requests",
                            description: "You have sent too many requests in a given amount of time ('rate limiting')."
                        }
                    ]
                },
                {
                    title: "5xx Server Errors",
                    subItems: [
                        {
                            title: "500 Internal Server Error",
                            description: "The server encountered an unexpected condition that prevented it from fulfilling the request. Please wait some time then try again."
                        },
                        {
                            title: "503 Service Unavailable",
                            description: "The server is not ready to handle the request, often due to maintenance or overload."
                        },
                        {
                            title: "504 Gateway Timeout",
                            description: "The server, while acting as a gateway or proxy, did not receive a timely response from the upstream server."
                        }
                    ]
                }
            ]
        },
    ],
    errorDescriptionModel: [errorResponseBody, errorJsonResponse],
    webhookErrors: [
        {
            heading: "Common Webhook Errors",
            info: [
                "The errors you may encounter when using the webhooks will look different than the endpoints. Our webhooks will trigger on each event. Below are some possible errors you might encounter when using our webhooks."
            ],
            items: [
                {
                    question: "The webhook has stopped even though I have received payloads.",
                    answer: "We require an HTTP success code from your API after we send a payload. This is to ensure that we are not sending your SafeSend data to a server that is not operational. This is to prevent data loss. Ensure that your API sends us a 200 (2xx) response code after every payload received. We hold all delivered payloads in a queue when a previously active webhook stops for any reason. Once it resumes, we will send all the payloads in the queue to the endpoint specified."
                },
                {
                    question: "I am not receiving payloads when I think I should.",
                    answer: "Please ensure the webhook is set up correctly and your API can handle the incoming data we will send you. We will automatically turn off the webhook event subscription if we do not receive a success message. If your webhook subscription is still active then your API is telling us you are receiving the payloads."
                }
            ]
        }
    ],
    rateLimiting: [
        {
            heading: "Introduction",
            info: [
                "At SafeSend, API rate limiting is a critical mechanism to control the volume of requests users can make to our APIs within a specified time window. By implementing rate limiting, we ensure that our services remain available, responsive, and efficient for all users."
            ]
        },
        {
            heading: "Why Rate Limiting is Important",
            items: [
                {
                    title: "Service Stability",
                    description: "Rate limiting helps to maintain the overall stability of our APIs by preventing excessive load on the servers. Without it, a spike in requests could overload the system, causing performance degradation."
                },
                {
                    title: "Enhanced Security",
                    description: "Rate limiting helps to mitigate abuse, such as denial-of-service attacks, brute-force attempts, or other malicious activities. By limiting the number of requests, we add a layer of protection that helps identify and control suspicious behavior for the benefit of all users."
                }
            ]
        },
        {
            heading: "Rate Limiting Guidelines",
            info: [
                "To manage traffic effectively and ensure a fair and stable API environment, we have implemented the following rate limits.",
            ],
            note: 'The following limits apply to all endpoints within our API, unless otherwise specified.'
        },
        {
            heading: "Standard Rate Limit",
            items: [
                {
                    title: "Limit",
                    description: "X requests per Y seconds."
                },
                {
                    title: "Response",
                    description: " If the rate limit is exceeded, a 429 Too Many Requests status code is returned, along with a message specifying the time remaining until the limit resets."
                }
            ],
            note: 'API rate limits will not apply to SafeSend webhooks responses.'
        },
    ]
};